(() => {
  /**
   * @typedef {{ label: string, valueHTML: string }} SpecificationRow
   */

  /**
   * @typedef {{ heading: string, rows: SpecificationRow[] }} SpecificationGroup
   */

  /** @type {string} */
  const CONTENT_SELECTOR = '.tabs__panel .page__content.rte.text-subtext, .accordion-details__content.rte.text-subtext';
  /** @type {RegExp} */
  const SEPARATOR_PATTERN = /:\s+|\s[–—-]\s+/;

  /**
   * @param {string | null | undefined} text
   * @returns {string}
   */
  function normalizeText(text) {
    return (text || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {DocumentFragment} fragment
   * @returns {string}
   */
  function fragmentToHTML(fragment) {
    /** @type {HTMLDivElement} */
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML.trim();
  }

  /**
   * @param {HTMLElement} container
   * @returns {void}
   */
  function stripLeadingSeparator(container) {
    while (container.firstChild) {
      /** @type {ChildNode} */
      const node = container.firstChild;

      if (node.nodeType === Node.TEXT_NODE) {
        const value = node.textContent.replace(/^[\s\u00a0:–—-]+/, '');
        if (value.length) {
          node.textContent = value;
          return;
        }

        node.remove();
        continue;
      }

      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
        node.remove();
        continue;
      }

      return;
    }
  }

  /**
   * @param {HTMLElement} container
   * @returns {ChildNode | null}
   */
  function getFirstMeaningfulNode(container) {
    return Array.from(container.childNodes).find((node) => normalizeText(node.textContent).length > 0) || null;
  }

  /**
   * @param {Element} element
   * @returns {HTMLDivElement[]}
   */
  function splitElementIntoLines(element) {
    /** @type {HTMLDivElement[]} */
    const lines = [];
    /** @type {HTMLDivElement} */
    let currentLine = document.createElement('div');

    Array.from(element.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
        if (normalizeText(currentLine.textContent)) {
          lines.push(currentLine);
        }
        currentLine = document.createElement('div');
        return;
      }

      currentLine.appendChild(node.cloneNode(true));
    });

    if (normalizeText(currentLine.textContent)) {
      lines.push(currentLine);
    }

    return lines;
  }

  /**
   * @param {HTMLElement} line
   * @returns {SpecificationRow | null}
   */
  function extractRowFromLine(line) {
    const firstNode = getFirstMeaningfulNode(line);

    if (firstNode?.nodeType === Node.ELEMENT_NODE && firstNode.tagName === 'STRONG') {
      const label = normalizeText(firstNode.textContent).replace(/[:–—-]+$/, '').trim();

      if (!label) return null;

      const valueContainer = line.cloneNode(true);
      const strong = valueContainer.querySelector('strong');
      strong && strong.remove();
      stripLeadingSeparator(valueContainer);

      const valueText = normalizeText(valueContainer.textContent);
      if (!valueText) return null;

      return {
        label,
        valueHTML: valueContainer.innerHTML.trim(),
      };
    }

    /** @type {TreeWalker} */
    const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
    /** @type {Node | null} */
    let textNode;

    while ((textNode = walker.nextNode())) {
      const match = textNode.textContent.match(SEPARATOR_PATTERN);
      if (!match) continue;

      const labelRange = document.createRange();
      labelRange.selectNodeContents(line);
      labelRange.setEnd(textNode, match.index);

      const valueRange = document.createRange();
      valueRange.selectNodeContents(line);
      valueRange.setStart(textNode, match.index + match[0].length);

      const label = normalizeText(labelRange.toString());
      const valueText = normalizeText(valueRange.toString());

      if (!label || !valueText) return null;

      return {
        label,
        valueHTML: fragmentToHTML(valueRange.cloneContents()),
      };
    }

    return null;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  function isEmptyParagraph(element) {
    return element.tagName === 'P' && normalizeText(element.textContent).length === 0;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  function isGroupHeading(element) {
    if (element.tagName !== 'P') return false;

    /** @type {ChildNode[]} */
    const meaningfulChildren = Array.from(element.childNodes).filter((node) => normalizeText(node.textContent).length > 0);
    if (meaningfulChildren.length !== 1) return false;

    const [node] = meaningfulChildren;
    if (node.nodeType !== Node.ELEMENT_NODE || node.tagName !== 'STRONG') return false;

    const heading = normalizeText(node.textContent).replace(/[:–—-]+$/, '').trim();
    return heading.length > 0;
  }

  /**
   * @param {Element} element
   * @returns {SpecificationRow[] | null}
   */
  function parseRowsFromElement(element) {
    /** @type {Element[] | HTMLDivElement[]} */
    const lineSources =
      element.tagName === 'UL' || element.tagName === 'OL'
        ? Array.from(element.children)
        : splitElementIntoLines(element);

    if (!lineSources.length) return null;

    /** @type {SpecificationRow[]} */
    const rows = [];

    for (const lineSource of lineSources) {
      const lines = lineSource.tagName === 'LI' ? splitElementIntoLines(lineSource) : [lineSource];

      for (const line of lines) {
        if (!normalizeText(line.textContent)) continue;

        const row = extractRowFromLine(line);
        if (!row) return null;
        rows.push(row);
      }
    }

    return rows.length ? rows : null;
  }

  /**
   * @param {Element} content
   * @returns {SpecificationGroup[] | null}
   */
  function parseSpecificationGroups(content) {
    /** @type {SpecificationGroup[]} */
    const groups = [];
    /** @type {SpecificationGroup | null} */
    let currentGroup = null;
    /** @type {number} */
    let rowCount = 0;
    /** @type {boolean} */
    let hasHeading = false;

    for (const element of Array.from(content.children)) {
      if (isEmptyParagraph(element)) continue;

      if (isGroupHeading(element)) {
        currentGroup = {
          heading: normalizeText(element.textContent).replace(/[:–—-]+$/, '').trim(),
          rows: [],
        };
        hasHeading = true;
        groups.push(currentGroup);
        continue;
      }

      if (!['P', 'UL', 'OL'].includes(element.tagName)) {
        return null;
      }

      const rows = parseRowsFromElement(element);
      if (!rows) return null;

      if (!currentGroup) {
        currentGroup = { heading: '', rows: [] };
        groups.push(currentGroup);
      }

      currentGroup.rows.push(...rows);
      rowCount += rows.length;
    }

    /** @type {SpecificationGroup[]} */
    const validGroups = groups.filter((group) => group.rows.length);
    if (!validGroups.length) return null;

    if (rowCount < 2 || (!hasHeading && rowCount < 3)) {
      return null;
    }

    return validGroups;
  }

  /**
   * @param {SpecificationGroup[]} groups
   * @returns {HTMLDivElement}
   */
  function buildSpecificationMarkup(groups) {
    /** @type {HTMLDivElement} */
    const wrapper = document.createElement('div');
    wrapper.className = 'specifications-content';

    groups.forEach((group) => {
      /** @type {HTMLElement} */
      const groupElement = document.createElement('section');
      groupElement.className = 'specifications-group';

      if (group.heading) {
        /** @type {HTMLParagraphElement} */
        const heading = document.createElement('p');
        heading.className = 'specifications-group__heading';

        /** @type {HTMLElement} */
        const strong = document.createElement('strong');
        strong.textContent = group.heading;

        heading.appendChild(strong);
        groupElement.appendChild(heading);
      }

      /** @type {HTMLDivElement} */
      const attributes = document.createElement('div');
      attributes.className = 'attributes specifications-table';

      group.rows.forEach((row) => {
        /** @type {HTMLDivElement} */
        const attribute = document.createElement('div');
        attribute.className = 'attribute product-attribute';

        /** @type {HTMLDivElement} */
        const name = document.createElement('div');
        name.className = 'product-attribute-name';
        name.textContent = row.label;

        /** @type {HTMLDivElement} */
        const value = document.createElement('div');
        value.className = 'product-attribute-value';
        value.innerHTML = row.valueHTML;

        /** @type {HTMLDivElement} */
        const clear = document.createElement('div');
        clear.className = 'clear';

        attribute.append(name, value, clear);
        attributes.appendChild(attribute);
      });

      groupElement.appendChild(attributes);
      wrapper.appendChild(groupElement);
    });

    return wrapper;
  }

  /**
   * @param {HTMLElement} content
   * @returns {void}
   */
  function transformContent(content) {
    if (content.dataset.specificationsTransformed === 'true') return;
    if (content.querySelector('.attributes')) return;
    if (content.querySelector('iframe, video, model-viewer, .shopify-model-viewer-ui')) return;

    const groups = parseSpecificationGroups(content);
    if (!groups) return;

    content.innerHTML = '';
    content.appendChild(buildSpecificationMarkup(groups));
    content.dataset.specificationsTransformed = 'true';
  }

  /**
   * @param {ParentNode} [root=document]
   * @returns {void}
   */
  function init(root = document) {
    root.querySelectorAll(CONTENT_SELECTOR).forEach(transformContent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
